import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageViewModel, LookupProxy } from '@/app/proxy/proxies';
import { LocalStorageService } from '../storage/local-storage.service';
import { firstValueFrom, of, ReplaySubject } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LanguageInitializerService {
  private readonly LANGUAGE_GUID_KEY = 'languageGuid';
  private readonly LANGUAGE_ISO1_KEY = 'languageISO1';

  private initializationSubject = new ReplaySubject<boolean>(1);
  private initializationComplete = false;
  private _languages: LanguageViewModel[] = [];

  constructor(
    private readonly translate: TranslateService,
    private readonly lookupProxy: LookupProxy,
    private readonly localStorageService: LocalStorageService,
  ) {}

  async initializeLanguage(): Promise<boolean> {
    if (this.initializationComplete) {
      return true;
    }

    return new Promise<boolean>((resolve) => {
      if (!this.initializationSubject.observed) {
        this.performLanguageInitialization();
      }

      this.initializationSubject.subscribe((success) => {
        this.initializationComplete = true;
        resolve(success);
      });
    });
  }

  private async performLanguageInitialization(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.lookupProxy.lookup_GetLanguages().pipe(
          catchError((error) => {
            console.error('Error initializing language:', error);
            return of(null);
          }),
        ),
      );

      if (response?.result) {
        this.configureLanguages(response.result);
      } else {
        this.setFallbackLanguage();
      }
    } catch (error) {
      console.error('Error initializing language:', error);
      this.setFallbackLanguage();
    } finally {
      this.initializationSubject.next(true);
      this.initializationSubject.complete();
    }
  }

  private configureLanguages(languages: LanguageViewModel[]): void {
    this._languages = languages;

    this.migrateOldLanguageKey(languages);

    if (this.localStorageService.getItem(this.LANGUAGE_GUID_KEY) == null) {
      const defaultLang = languages.find((c) => c.isO1 === 'en');

      if (defaultLang) {
        this.localStorageService.setItem(this.LANGUAGE_GUID_KEY, defaultLang.id);
        this.localStorageService.setItem(this.LANGUAGE_ISO1_KEY, defaultLang.isO1);
      } else {
        const fallbackLang = languages[0];
        if (fallbackLang) {
          this.localStorageService.setItem(this.LANGUAGE_GUID_KEY, fallbackLang.id);
          this.localStorageService.setItem(this.LANGUAGE_ISO1_KEY, fallbackLang.isO1);
        }
      }
    }

    const langIds = languages.map((c) => c.id);
    this.translate.addLangs(langIds ?? []);

    const currentLangGuid = this.getCurrentLanguageGuid();
    const currentLangISO1 = this.getCurrentLanguageCode();

    if (!localStorage.getItem(this.LANGUAGE_ISO1_KEY)) {
      localStorage.setItem(this.LANGUAGE_ISO1_KEY, currentLangISO1);
    }

    this.translate.setFallbackLang(currentLangGuid);
    this.translate.use(currentLangGuid);
  }

  private setFallbackLanguage(): void {
    const fallbackGuid = '24c9b00d-d028-4e04-a1aa-f04d2dcae2c3';
    const fallbackISO1 = 'en';

    this.localStorageService.setItem(this.LANGUAGE_GUID_KEY, fallbackGuid);
    this.localStorageService.setItem(this.LANGUAGE_ISO1_KEY, fallbackISO1);

    this.translate.addLangs([fallbackGuid]);
    this.translate.setFallbackLang(fallbackGuid);
    this.translate.use(fallbackGuid);
  }

  getCurrentLanguageGuid(): string {
    return (
      this.localStorageService.getItem<string>(this.LANGUAGE_GUID_KEY) ??
      '24c9b00d-d028-4e04-a1aa-f04d2dcae2c3'
    );
  }

  getCurrentLanguageCode(): string {
    return this.localStorageService.getItem<string>(this.LANGUAGE_ISO1_KEY) ?? 'en';
  }

  getLanguages(): LanguageViewModel[] {
    return this._languages;
  }

  setLanguage(languageId: string): void {
    const selectedLanguage = this._languages.find((lang) => lang.id === languageId);

    if (!selectedLanguage) {
      console.error(`Language with ID ${languageId} not found in available languages`);
      return;
    }

    this.localStorageService.setItem(this.LANGUAGE_GUID_KEY, selectedLanguage.id);
    this.localStorageService.setItem(this.LANGUAGE_ISO1_KEY, selectedLanguage.isO1);

    this.translate.setFallbackLang(languageId);
    this.translate.use(languageId);
    this.translate.reloadLang(languageId);
  }

  setLanguageByISO1(iso1Code: string): void {
    const selectedLanguage = this._languages.find((lang) => lang.isO1 === iso1Code);

    if (selectedLanguage) {
      this.setLanguage(selectedLanguage.id);
    } else {
      console.error(`Language with ISO1 code ${iso1Code} not found in available languages`);
    }
  }

  getCurrentLanguageInfo(): { guid: string; iso1: string } {
    return {
      guid: this.getCurrentLanguageGuid(),
      iso1: this.getCurrentLanguageCode(),
    };
  }

  getLocaleFromISO1(iso1Code?: string): string {
    const code = (iso1Code || this.getCurrentLanguageCode() || 'en').toLowerCase();

    if (typeof Intl !== 'undefined' && (Intl as any).Locale) {
      try {
        const locale = new (Intl as any).Locale(code);
        if (locale.region) {
          return `${locale.language}-${locale.region}`;
        }
      } catch {
        // ignore
      }
    }

    const browserLocales = navigator.languages || [navigator.language || 'en-US'];
    for (const browserLocale of browserLocales) {
      const browserLang = browserLocale.split('-')[0].toLowerCase();
      if (code === browserLang) {
        return browserLocale;
      }
    }

    try {
      const formatter = new Intl.DateTimeFormat(code, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const resolvedLocale = formatter.resolvedOptions().locale;
      if (resolvedLocale && resolvedLocale !== code) {
        return resolvedLocale;
      }
    } catch {
      // ignore
    }

    return code;
  }

  private migrateOldLanguageKey(languages: LanguageViewModel[]): void {
    const oldLanguageKey = this.localStorageService.getItem<string>('language');
    const hasNewKeys = this.localStorageService.getItem(this.LANGUAGE_GUID_KEY) !== null;

    if (oldLanguageKey && !hasNewKeys) {
      const languageObj = languages.find((lang) => lang.id === oldLanguageKey);

      if (languageObj) {
        this.localStorageService.setItem(this.LANGUAGE_GUID_KEY, languageObj.id);
        this.localStorageService.setItem(this.LANGUAGE_ISO1_KEY, languageObj.isO1);
        this.localStorageService.removeItem('language');
      }
    }
  }
}

